import { getCustomModelTexts } from 'src/data/customModel';
import {
  DRAFT_TOV_ID,
  formatToneOfVoiceDescription,
  getToneOfVoiceForGeneration,
  getToneOfVoiceTitle,
  getTovTexts,
  isPresetTov,
  type PresetToneOfVoice,
} from 'src/data/toneOfVoice';
import { STRUCTURE_INSTRUCTIONS_APPLY_LABEL } from 'src/data/ui';
import type { ID } from 'src/lib/collection';

import {
  AI_RECOMPOSE_MODE,
  type AiRecomposeMode,
  GENERATION_IMPROVE_MODE,
  type GenerationImproveMode,
  type Language,
} from './const';
import type { ImageStyleListData, ImproveParams, ToneOfVoiceListData, TovInput } from './types';

const MAKE_CASUAL_INSTRUCTIONS = 'Use more casual terminology but keep it similar.';
const MAKE_LONGER_INSTRUCTIONS = 'make it longer';
const MAKE_SHORTER_INSTRUCTIONS = 'make it shorter';

interface GenerationImproveParams {
  improveMode: GenerationImproveMode;
  recomposeMode?: AiRecomposeMode;
  instructions?: string;
  tovInput?: TovInput;
  media?: MediaGenerationParameters;
}

interface MediaGenerationParameters {
  imageStyleId?: ID;
}

const getTovInput = (improveParams: ImproveParams): TovInput | undefined => {
  if (improveParams.improveMode !== GENERATION_IMPROVE_MODE.changeTov || !improveParams.tov) {
    return undefined;
  }

  const { tov, customModelId, draftToneOfVoice } = improveParams.tov;
  return getToneOfVoiceForGeneration(customModelId || tov, draftToneOfVoice);
};

/**
 * Converts improve parameters to the generation parameters of a short-form recompose: the editor's
 * generation tools, its short-form assets and the Chrome extension.
 */
export const getShortFormImproveParams = (
  improveParams: ImproveParams
): GenerationImproveParams => {
  let instructions = improveParams.instructions;
  if (improveParams.improveMode === GENERATION_IMPROVE_MODE.makeCasual) {
    instructions = MAKE_CASUAL_INSTRUCTIONS;
  } else if (improveParams.improveMode === GENERATION_IMPROVE_MODE.makeLonger) {
    instructions = MAKE_LONGER_INSTRUCTIONS;
  } else if (improveParams.improveMode === GENERATION_IMPROVE_MODE.makeShorter) {
    instructions = MAKE_SHORTER_INSTRUCTIONS;
  }

  let media;
  if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeImageStyle) {
    media = {
      imageStyleId: improveParams.imageStyleId,
    };
  }

  return {
    improveMode: improveParams.improveMode,
    recomposeMode: generationImproveModeToRecomposeModeShortForm(improveParams.improveMode),
    instructions,
    tovInput: getTovInput(improveParams),
    media,
  };
};

/**
 * Converts improve parameters to the generation parameters of a long-form recompose: the editor's
 * long-form assets and the blog builder. Making it longer or shorter has recompose modes of its
 * own here, so it needs no instructions.
 */
export const getLongFormImproveParams = (
  improveParams: ImproveParams
): GenerationImproveParams => ({
  improveMode: improveParams.improveMode,
  recomposeMode: generationImproveModeToRecomposeModeLongForm(improveParams.improveMode),
  instructions:
    improveParams.improveMode === GENERATION_IMPROVE_MODE.makeCasual
      ? MAKE_CASUAL_INSTRUCTIONS
      : improveParams.instructions,
  tovInput: getTovInput(improveParams),
});

export const formatImproveParamsDescription = (
  improveParams: ImproveParams,
  {
    ToneOfVoiceListProps,
    ImageStyleListProps,
    language,
    getTitle,
  }: {
    ToneOfVoiceListProps: ToneOfVoiceListData;
    ImageStyleListProps: ImageStyleListData;
    language: Language;
    getTitle?: (mode: GenerationImproveMode) => string;
  }
) => {
  if (!improveParams?.improveMode) {
    return '';
  }

  const title = getTitle
    ? getTitle(improveParams.improveMode)
    : getGenerationImproveModeLabel(improveParams.improveMode);

  // in case of "manual instructions", just return the instructions
  if (improveParams.improveMode === GENERATION_IMPROVE_MODE.instructions) {
    return improveParams.instructions || title;
  }

  let description: string | undefined = '';
  if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeTov && improveParams.tov) {
    const { tov, customModelId, draftToneOfVoice } = improveParams.tov;

    if (tov === DRAFT_TOV_ID && draftToneOfVoice) {
      description = formatToneOfVoiceDescription(draftToneOfVoice);
    } else if (customModelId) {
      const { customModelTitle } = getCustomModelTexts(
        customModelId,
        ToneOfVoiceListProps.customModels
      );
      description = customModelTitle;
    } else if (tov) {
      if (isPresetTov(tov as string)) {
        description = getToneOfVoiceTitle(tov as PresetToneOfVoice, language);
      } else {
        const { tovTitle } = getTovTexts(tov, ToneOfVoiceListProps.toneOfVoices, language);
        description = tovTitle;
      }
    }
  } else if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeImageStyle) {
    const { generalStyles, customStyles } = ImageStyleListProps;
    const imageStyle = [...generalStyles, ...customStyles].find(
      (style) => style.id === improveParams.imageStyleId
    );
    description = imageStyle?.name || '';
  }

  return description ? `${title}: ${description}` : title;
};

const IMPROVE_RECOMPOSE_SHORT_FORM_MAP: Partial<Record<GenerationImproveMode, AiRecomposeMode>> = {
  [GENERATION_IMPROVE_MODE.doNotImprove]: AI_RECOMPOSE_MODE.keepAsIs,
  [GENERATION_IMPROVE_MODE.improveImage]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.changeImageOverlay]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.changeImageStyle]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.improveEngaging]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.improveSimilar]: AI_RECOMPOSE_MODE.strict,
  [GENERATION_IMPROVE_MODE.boostScoreEngaging]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.boostScoreSimilar]: AI_RECOMPOSE_MODE.strict,
  [GENERATION_IMPROVE_MODE.makeCasual]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.makeLonger]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.makeShorter]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.changeTov]: AI_RECOMPOSE_MODE.tov,
  [GENERATION_IMPROVE_MODE.instructions]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.sfv]: AI_RECOMPOSE_MODE.sfv,
  [GENERATION_IMPROVE_MODE.formatGroups]: AI_RECOMPOSE_MODE.formatGroups,
};
export const generationImproveModeToRecomposeModeShortForm = (
  improveMode: GenerationImproveMode
): AiRecomposeMode | undefined => IMPROVE_RECOMPOSE_SHORT_FORM_MAP[improveMode];

const IMPROVE_RECOMPOSE_LONG_FORM_MAP: Partial<Record<GenerationImproveMode, AiRecomposeMode>> = {
  [GENERATION_IMPROVE_MODE.improveImage]: AI_RECOMPOSE_MODE.refine,
  [GENERATION_IMPROVE_MODE.changeImageOverlay]: AI_RECOMPOSE_MODE.refine,
  [GENERATION_IMPROVE_MODE.changeImageStyle]: AI_RECOMPOSE_MODE.refine,
  [GENERATION_IMPROVE_MODE.improveEngaging]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.improveSimilar]: AI_RECOMPOSE_MODE.strict,
  [GENERATION_IMPROVE_MODE.boostScoreEngaging]: AI_RECOMPOSE_MODE.refine,
  [GENERATION_IMPROVE_MODE.boostScoreSimilar]: AI_RECOMPOSE_MODE.strict,
  [GENERATION_IMPROVE_MODE.makeCasual]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.makeLonger]: AI_RECOMPOSE_MODE.expand,
  [GENERATION_IMPROVE_MODE.makeShorter]: AI_RECOMPOSE_MODE.shorten,
  [GENERATION_IMPROVE_MODE.changeTov]: AI_RECOMPOSE_MODE.tov,
  [GENERATION_IMPROVE_MODE.instructions]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.sfv]: AI_RECOMPOSE_MODE.sfv,
  [GENERATION_IMPROVE_MODE.formatGroups]: AI_RECOMPOSE_MODE.formatGroups,
};
const generationImproveModeToRecomposeModeLongForm = (
  improveMode: GenerationImproveMode
): AiRecomposeMode | undefined => IMPROVE_RECOMPOSE_LONG_FORM_MAP[improveMode];

const GENERATION_IMPROVE_MODE_PROPS: Record<string, { label: string; description?: string }> = {
  [GENERATION_IMPROVE_MODE.doNotImprove]: {
    label: 'Keep as is',
  },
  [GENERATION_IMPROVE_MODE.continueWriting]: {
    label: 'Continue writing',
  },
  [GENERATION_IMPROVE_MODE.improveImage]: {
    label: 'Improve image performance',
    description: 'Make more engaging',
  },
  [GENERATION_IMPROVE_MODE.changeImageOverlay]: {
    label: 'Edit overlay text', // note label changes in `ChangeImageOverlayItem`
  },
  [GENERATION_IMPROVE_MODE.changeImageStyle]: {
    label: 'Change image style',
  },
  [GENERATION_IMPROVE_MODE.improveEngaging]: {
    label: 'Make more engaging',
  },
  [GENERATION_IMPROVE_MODE.improveSimilar]: {
    label: 'Make more engaging, but keep structure',
  },
  [GENERATION_IMPROVE_MODE.boostScoreEngaging]: {
    label: 'Generate higher scoring content',
    description: 'Make more engaging',
  },
  [GENERATION_IMPROVE_MODE.boostScoreSimilar]: {
    label: 'Generate higher scoring content, but keep structure',
    description: 'Fix spelling & grammar, replace a word where necessary',
  },
  [GENERATION_IMPROVE_MODE.makeCasual]: {
    label: 'Make more casual',
  },
  [GENERATION_IMPROVE_MODE.makeLonger]: {
    label: 'Make longer',
  },
  [GENERATION_IMPROVE_MODE.makeShorter]: {
    label: 'Make shorter',
  },
  [GENERATION_IMPROVE_MODE.changeTov]: {
    label: 'Change tone of voice',
  },
  [GENERATION_IMPROVE_MODE.sfv]: {
    label: STRUCTURE_INSTRUCTIONS_APPLY_LABEL,
  },
  [GENERATION_IMPROVE_MODE.formatGroups]: {
    label: 'Apply AP style guide',
  },
  [GENERATION_IMPROVE_MODE.instructions]: {
    label: 'Manual instructions',
  },
};

export const getGenerationImproveModeLabel = (type: GenerationImproveMode): string =>
  GENERATION_IMPROVE_MODE_PROPS[type]?.label;
export const getGenerationImproveModeDescription = (
  type: GenerationImproveMode
): string | undefined => GENERATION_IMPROVE_MODE_PROPS[type]?.description;

export const doesImproveModeAlwaysGenerateSingleVariation = (
  improveMode: GenerationImproveMode
): boolean => {
  const list: GenerationImproveMode[] = [
    GENERATION_IMPROVE_MODE.sfv,
    GENERATION_IMPROVE_MODE.formatGroups,
  ];
  return !list.includes(improveMode);
};

export const IMPROVE_SCORE_MODES: GenerationImproveMode[] = [
  GENERATION_IMPROVE_MODE.boostScoreEngaging,
  GENERATION_IMPROVE_MODE.boostScoreSimilar,
];

export const shouldImproveScoresForMode = (improveMode: GenerationImproveMode): boolean =>
  IMPROVE_SCORE_MODES.includes(improveMode);
